import { AloftPrice, schAloftPrice } from '@aloft/toolkit-shared';

import { useAlerts } from '../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import {
  selectCommentary,
  selectCommentaryV2,
} from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOpinionOfSiteValuePrice } from '../../redux/features/selectors/opinionOfSiteValueSelectors.ts';
import { selectCompSequence, selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { selectImprovementsEntities } from '../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationById } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectReconciliation } from '../../redux/features/selectors/selectReconciliation.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { schAppraisifyValidation } from './schemas.ts';
import { OrganizedValidationErrors, ValidationErrorType, ValidationKey } from './types.ts';

export const useAppraisifyValidation = () => {
  const subjectPropertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const order = useAppSelector(selectOrder);
  const compSequence = useAppSelector(selectCompSequence);
  const improvements = useAppSelector(selectImprovementsEntities);
  const opinionOfSiteValue = useAppSelector(selectOpinionOfSiteValuePrice);
  const { marketConditionsV2, priorSales } = useAppSelector(selectCommentaryV2);
  const {
    totalCostToCure,
    opinionOfMarketValue,
    opinionOfAfterRepairValue,
    totalRenovationBudget,
    includeTotalCostToCure,
    includeTotalRenovationBudget,
    includeOpinionOfAfterRepairValue,
    valueTypes,
  } = useAppSelector(selectReconciliation);

  const { reconciliation, alerts: alertsCommentary } = useAppSelector(selectCommentary);
  const feasibilityCommentary = reconciliation?.feasibilityCommentary;

  const alerts = useAlerts();
  const allAlerts = Object.values(alerts).flat();

  const isArvOrder = order.hasArvOption;
  const isMockOrder = order.isMock;
  const estimatedROI =
    isArvOrder && opinionOfAfterRepairValue && opinionOfMarketValue && totalRenovationBudget
      ? schAloftPrice
          .parse(opinionOfAfterRepairValue)
          .sub(schAloftPrice.parse(opinionOfMarketValue))
          .sub(schAloftPrice.parse(totalRenovationBudget))
      : null;

  const shouldShowFeasibilityCommentary =
    isArvOrder && estimatedROI !== null && estimatedROI.toDollarLike() <= 0;

  const validationForm = schAppraisifyValidation.safeParse({
    alerts: {
      alerts: allAlerts,
    },
    orderDetails: {
      zoning: subjectPropertyIdentification?.zoning ?? '',
      zoningCompliance: subjectPropertyIdentification?.zoningCompliance ?? '',
      parcelNumber: subjectPropertyIdentification?.parcelNumber ?? '',
      newConstruction: subjectPropertyIdentification?.newConstruction ?? false,
      legalDescription: subjectPropertyIdentification?.legalDescription ?? '',
      hoa: subjectPropertyIdentification?.hoa ?? '',
      effectiveDate: order.effectiveDate ?? '',
      intendedUsers: order.intendedUsers ?? '',
      intendedUse: order.intendedUse ?? '',
      propertyInterest: subjectPropertyIdentification?.propertyInterest ?? '',
      propertyInterestOther: subjectPropertyIdentification?.propertyInterestOther ?? '',
    },
    market: {},
    analysis: {
      comps: compSequence ?? [],
      opinionOfSiteValue: opinionOfSiteValue ?? '',
    },
    commentary: {
      estimatedDemand: marketConditionsV2?.estimatedDemandSupply ?? '',
      estimatedExposureTime: marketConditionsV2?.estimatedExposureTime ?? '',
      marketConditionCommentary: marketConditionsV2?.commentary ?? '',
      priorSalesCommentary: priorSales?.commentary ?? '',
    },
    reconciliation: {
      totalCostToCure,
      opinionOfMarketValue,
      opinionOfAfterRepairValue,
      totalRenovationBudget,
      includeTotalRenovationBudget: includeTotalRenovationBudget ?? false,
      includeOpinionOfAfterRepairValue: includeOpinionOfAfterRepairValue ?? false,
      includeTotalCostToCure: includeTotalCostToCure ?? false,
      hasArvOption: order.hasArvOption,
      reconciliationCommentary: reconciliation?.reconciliationCommentary ?? '',
      highestAndBestUseCommentary: reconciliation?.highestAndBestUseCommentary ?? '',
      revisionCommentary: reconciliation?.revisionCommentary ?? '',
      alternativeComparablesCommentary: reconciliation?.alternativeComparablesCommentary ?? [],
      feasibilityCommentary: feasibilityCommentary ?? '',
      valueTypes: valueTypes ?? [],
    },
  });

  const processValidationErrors = (): OrganizedValidationErrors => {
    const organizedErrors: OrganizedValidationErrors = {
      Alerts: {},
      OrderDetails: {},
      Analysis: {},
      Market: {},
      Commentary: {},
      Reconciliation: {},
      Global: {},
    };

    if (!order?.isInspectifyOrder) {
      return organizedErrors;
    }

    const dismissedAlerts = allAlerts.filter(({ status }) => status === 'dismissed');
    if (dismissedAlerts.length > 0) {
      const hasDismissedAlertsWithoutCommentary = dismissedAlerts.some(
        (alert) =>
          alertsCommentary?.[alert.type] === undefined ||
          alertsCommentary?.[alert.type]?.trim() === '',
      );

      if (hasDismissedAlertsWithoutCommentary) {
        organizedErrors.Alerts[ValidationKey.AlertsCommentary] = {
          type: ValidationErrorType.REQUIRED,
          message: 'Please provide commentary for all dismissed alerts.',
        };
      }
    }

    const subjectQuality = improvements?.subject?.craftsmanQuality;
    const hasMissingQuality =
      !subjectQuality ||
      (compSequence && compSequence.some((compId) => !improvements?.[compId]?.craftsmanQuality));

    if (hasMissingQuality) {
      organizedErrors.Analysis.costQuality = {
        type: ValidationErrorType.REQUIRED,
        message: 'Required',
      };
    }

    if (validationForm.error) {
      validationForm.error.issues.forEach((issue) => {
        const { path, code } = issue;
        let { message } = issue;

        if (path[path.length - 1] === 'revisionCommentary' && !order.isRevision) {
          return;
        }

        if (
          path[path.length - 1] === 'alternativeComparablesCommentary' &&
          (!order.isRevision || !order.revisionData?.comparables)
        ) {
          return;
        }

        if (path[path.length - 1] === 'totalCostToCure' && !includeTotalCostToCure) {
          return;
        }

        if (path[path.length - 1] === 'feasibilityCommentary' && !shouldShowFeasibilityCommentary) {
          return;
        }

        if (path[path.length - 1] === 'totalRenovationBudget' && !includeTotalRenovationBudget) {
          return;
        }

        if (
          path[path.length - 1] === 'opinionOfAfterRepairValue' &&
          !includeOpinionOfAfterRepairValue
        ) {
          return;
        }

        if (
          path[path.length - 1] === 'feasibilityCommentary' &&
          opinionOfAfterRepairValue &&
          opinionOfMarketValue
        ) {
          const renovationBudget = totalRenovationBudget || AloftPrice.zero;
          const roi = AloftPrice.schema
            .parse(opinionOfAfterRepairValue)
            .sub(AloftPrice.schema.parse(opinionOfMarketValue))
            .sub(AloftPrice.schema.parse(renovationBudget));
          if (roi.toDollarLike() > 0) {
            return;
          }
        }

        if (path.length >= 2) {
          const module = path[0] as string;
          const field = path[1] as string;

          let validationKey: ValidationKey | null = null;
          let targetModule: keyof OrganizedValidationErrors | null = null;

          switch (module) {
            case 'alerts':
              targetModule = 'Alerts';
              switch (field) {
                case 'alerts':
                  validationKey = ValidationKey.Alerts;
                  break;
                default:
                  break;
              }
              break;
            case 'orderDetails':
              targetModule = 'OrderDetails';
              switch (field) {
                case 'zoning':
                  validationKey = ValidationKey.Zoning;
                  break;
                case 'zoningCompliance':
                  validationKey = ValidationKey.ZoningCompliance;
                  if (code === 'invalid_enum_value' && issue.received === '') {
                    message = 'Please select a zoning compliance option.';
                  }
                  break;
                case 'parcelNumber':
                  validationKey = ValidationKey.ParcelNumber;
                  break;
                case 'legalDescription':
                  validationKey = ValidationKey.LegalDescription;
                  break;
                case 'hoa':
                  validationKey = ValidationKey.NeighborhoodName;
                  break;
                case 'effectiveDate':
                  validationKey = ValidationKey.EffectiveDate;
                  break;
                case 'intendedUsers':
                  validationKey = ValidationKey.IntendedUsers;
                  break;
                case 'intendedUse':
                  validationKey = ValidationKey.IntendedUse;
                  break;
                case 'propertyInterest':
                  validationKey = ValidationKey.PropertyInterest;
                  if (code === 'invalid_enum_value' && issue.received === '') {
                    message = 'Please select a property interest option.';
                  }
                  break;
                case 'propertyInterestOther':
                  validationKey = ValidationKey.PropertyInterestOther;
                  break;
                default:
                  break;
              }
              break;
            case 'analysis':
              targetModule = 'Analysis';
              switch (field) {
                case 'comps':
                  validationKey = ValidationKey.Comps;
                  message = 'At least one comparable is required';
                  break;
                case 'opinionOfSiteValue':
                  validationKey = ValidationKey.OpinionOfSiteValue;
                  if (code === 'invalid_union')
                    message = 'Please enter a valid site value before continuing.';
                  break;
                default:
                  break;
              }
              break;
            case 'market':
              targetModule = 'Market';
              switch (field) {
                default:
                  break;
              }
              break;
            case 'commentary':
              targetModule = 'Commentary';
              switch (field) {
                case 'estimatedDemand':
                  validationKey = ValidationKey.EstimatedDemand;
                  break;
                case 'estimatedExposureTime':
                  validationKey = ValidationKey.EstimatedExposureTime;
                  break;
                case 'marketConditionCommentary':
                  validationKey = ValidationKey.MarketConditionCommentary;
                  break;
                case 'priorSalesCommentary':
                  validationKey = ValidationKey.PriorSalesCommentary;
                  break;
                default:
                  break;
              }
              break;
            case 'reconciliation':
              targetModule = 'Reconciliation';
              if (isMockOrder) {
                break;
              }
              switch (field) {
                case 'totalCostToCure':
                  validationKey = ValidationKey.TotalCostToCure;
                  message = 'Required';
                  break;
                case 'opinionOfMarketValue':
                  validationKey = ValidationKey.OpinionOfMarketValue;
                  message = 'Required';
                  break;
                case 'opinionOfAfterRepairValue':
                  validationKey = ValidationKey.OpinionOfAfterRepairValue;
                  message = 'Required';
                  break;
                case 'totalRenovationBudget':
                  validationKey = ValidationKey.TotalRenovationBudget;
                  message = 'Required';
                  break;
                case 'reconciliationCommentary':
                  validationKey = ValidationKey.ReconciliationCommentary;

                  break;
                case 'highestAndBestUseCommentary':
                  validationKey = ValidationKey.HighestAndBestUseCommentary;
                  break;
                case 'revisionCommentary':
                  validationKey = ValidationKey.RevisionCommentary;
                  break;
                case 'alternativeComparablesCommentary':
                  validationKey = ValidationKey.AlternativeComparablesCommentary;
                  break;
                case 'feasibilityCommentary':
                  message = 'Feasibility Commentary is required when ROI is negative';
                  validationKey = ValidationKey.FeasibilityCommentary;
                  break;
                case 'valueTypes':
                  message = 'Please select a value type option';
                  validationKey = ValidationKey.ValueTypes;
                  break;
                default:
                  break;
              }
              break;
            default:
              break;
          }

          if (validationKey && targetModule) {
            organizedErrors[targetModule][validationKey] = {
              type: ValidationErrorType.REQUIRED,
              message,
            };
          }
        }
      });
    }

    if (order.isEscalated) {
      organizedErrors.Global[ValidationKey.IsEscalated] = {
        type: ValidationErrorType.REQUIRED,
        message: 'Cannot certify this order while Appraisify escalates the issue to our client.',
      };
    }

    return organizedErrors;
  };

  const errors = processValidationErrors();

  return {
    errors,
  };
};
